import { useEffect, useState } from 'react';
import {
  communityPlatformLabel,
  communityPlatformTone,
  type CommunityChannel,
} from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { ListSkeleton } from '@/features/ui/skeleton';
import { CollapsibleCard } from '@/features/ui/CollapsibleCard';

export function CommunitiesPanel() {
  const [channels, setChannels] = useState<CommunityChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('community_channels')
        .select('id, platform, label, url, description, sort_order, is_active, created_at')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (cancelled) return;
      if (err) setError(err.message);
      else setChannels((data ?? []) as CommunityChannel[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <CollapsibleCard title="Communautés ISBD" defaultOpen collapsedHint="Chargement…">
        <ListSkeleton rows={3} />
      </CollapsibleCard>
    );
  }

  if (error) {
    // Table absente tant que la migration n’est pas appliquée — message discret
    return null;
  }

  if (channels.length === 0) {
    return (
      <CollapsibleCard
        title="Communautés ISBD"
        collapsedHint="Section repliée — dépliez pour les liens de communauté."
      >
        <p className="u-empty u-synth-section-desc">
          Les liens de communauté seront bientôt disponibles. Revenez plus tard.
        </p>
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      title="Communautés ISBD"
      badge={`${channels.length}`}
      collapsedHint="Section repliée — dépliez pour rejoindre WhatsApp, Facebook, etc."
    >
      <p className="muted u-synth-section-desc">
        Rejoignez nos espaces d’échange : WhatsApp, Facebook, TikTok, Telegram, LinkedIn…
      </p>
      <div className="u-modules" style={{ marginTop: '1rem' }}>
        {channels.map((ch) => {
          const tone = communityPlatformTone(ch.platform);
          return (
            <a
              key={ch.id}
              href={ch.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`u-module u-module--${tone}`}
            >
              <div className="u-module-top">
                <div className={`u-module-icon ${tone}`}>
                  {communityPlatformLabel(ch.platform).charAt(0)}
                </div>
              </div>
              <h3>{ch.label}</h3>
              <p>{ch.description || `Rejoindre ${communityPlatformLabel(ch.platform)}`}</p>
              <span className="u-module-cta">Rejoindre</span>
            </a>
          );
        })}
      </div>
    </CollapsibleCard>
  );
}
